import {
  fetchPrices,
  defaultPortionSelection,
  BoxPricesResponse,
  PortionSelection,
} from './fetchPrices'
import { useShouldFetchPricingConfig } from './usePricingConfig'

interface NumPersonsToBoxDescriptorsItem {
  promo_code_valid: boolean
  num_portions: number
  price_per_portion: string
  price_per_portion_discounted: string
  total: string
  totalAfterDiscount: string
}

type NumPersonsToBoxDescriptorsList = NumPersonsToBoxDescriptorsItem[]

interface NumPersonsToBoxDescriptors {
  [n: number]: NumPersonsToBoxDescriptorsList
}

interface MarketingPricingConfig {
  pricingConfig: BoxPricesResponse
  defaultPortionSelection: PortionSelection
  pricePerPortion?: string
  discountedPricePerPortion?: string
  portionDescription: string
  numPersonsToBoxDescriptors: NumPersonsToBoxDescriptors
  availableBoxSizes: number[]
}

export const useMarketingPricingConfig = (): MarketingPricingConfig | null => {
  const shouldFetch = useShouldFetchPricingConfig()
  if (!shouldFetch) {
    return null
  }

  const pricingConfig = fetchPrices()

  const portionDescription = `${defaultPortionSelection.recipes} x ${defaultPortionSelection.people}`

  const pricePerPortion =
    pricingConfig.data[defaultPortionSelection.people][defaultPortionSelection.recipes].gourmet
      .price_per_portion
  const discountedPricePerPortion =
    pricingConfig.data[defaultPortionSelection.people][defaultPortionSelection.recipes]?.gourmet
      .price_per_portion_discounted

  // Transform pricing data into numPersonsToBoxDescriptors format
  const numPersonsToBoxDescriptors = Object.keys(
    pricingConfig.data,
  ).reduce<NumPersonsToBoxDescriptors>((acc, people) => {
    const recipes = pricingConfig.data[people as keyof typeof pricingConfig.data]
    acc[Number(people)] = Object.keys(recipes).map((recipeCount) => {
      const gourmet = recipes[recipeCount as keyof typeof recipes].gourmet
      return {
        num_portions: Number(recipeCount),
        price_per_portion: gourmet.price_per_portion,
        price_per_portion_discounted: gourmet.price_per_portion_discounted,
        promo_code_valid: gourmet.promo_code_valid,
        total: gourmet.recipe_total,
        totalAfterDiscount: gourmet.recipe_total_discounted,
      }
    })
    return acc
  }, {})

  const availableBoxSizes = Object.keys(numPersonsToBoxDescriptors).map(Number)

  return {
    pricingConfig,
    defaultPortionSelection,
    pricePerPortion,
    discountedPricePerPortion,
    portionDescription,
    numPersonsToBoxDescriptors,
    availableBoxSizes,
  }
}
